import React, { useMemo, useEffect, useState, useCallback } from "react";
import { useTable, usePagination } from "react-table";
import { COLUMNS } from "./Column";
import { useAuth } from "../../store/Auth";
import Swal from "sweetalert2";
import EmptyList from "../../components/empty-list-2.webp";
import { base, get_agent_cmr_requests } from "../../constants/urls";
import { ClipLoader } from "react-spinners";
import { useNavigate, Link, useParams } from "react-router-dom";
import { AiOutlinePlus } from "react-icons/ai";
import Header from "../assist/Title";

export default function Table() {
  const { reference } = useParams();
  const [result, setResult] = useState([]);
  const columns = useMemo(() => COLUMNS, []);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentRecords, setCurrentRecords] = useState(0);
  
  // UI states
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    payment_status: "",
    owner_status: "",
    vehicle_status: ""
  });
  
  // API states to prevent fetch spam
  const [fetchParams, setFetchParams] = useState({ search: "", ...filters });

  const getAuth = useAuth((state) => state.auth);
  const navigate = useNavigate();

  const fetchData = useCallback(
    async ({ pageIndex, pageSize }) => {
      setLoading(true);
      let url = `${base}${get_agent_cmr_requests}?page=${pageIndex + 1}&limit=${pageSize}`;
      
      if (fetchParams.search) url += `&search=${encodeURIComponent(fetchParams.search)}`;
      if (fetchParams.status) url += `&request_status=${encodeURIComponent(fetchParams.status)}`;
      if (fetchParams.payment_status) url += `&payment_status=${encodeURIComponent(fetchParams.payment_status)}`;
      if (fetchParams.owner_status) url += `&owner_status=${encodeURIComponent(fetchParams.owner_status)}`;
      if (fetchParams.vehicle_status) url += `&vehicle_status=${encodeURIComponent(fetchParams.vehicle_status)}`;
      
      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + getAuth.token,
          },
        });
        const { data } = await response.json();
        
        const records = Array.isArray(data.records) 
          ? data.records.map((r, i) => ({ ...r, serial: (pageIndex * pageSize) + i + 1 })) 
          : [];
          
        setResult(records);
        setTotalRecords(data.totalRecords || 0);
        setTotalPages(data.totalPage || 0);
        setCurrentRecords(records.length);
      } catch (err) {
        let message = err?.response?.data?.message || "Unable to fetch request";
        Swal.fire(message);
      } finally {
        setLoading(false);
      }
    },
    [fetchParams, getAuth.token]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: result,
      initialState: { pageIndex: 0, pageSize: 10 },
      manualPagination: true,
      pageCount: totalPages,
    },
    usePagination
  );

  useEffect(() => {
    fetchData({ pageIndex, pageSize });
  }, [fetchData, pageIndex, pageSize]);

  const handleSearch = () => {
    setFetchParams((prev) => ({ ...prev, search: searchTerm }));
    gotoPage(0);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setFetchParams((prev) => ({ ...prev, [name]: value }));
    gotoPage(0);
  };

  const handleCellClick = (cell) => {
    const rowData = cell.row.original;
    if (cell.column.Header === "Action" && rowData.request_status === "Approved" && rowData.license_plate !== "") {
      if (rowData.request_type === "Motor Vehicle Information") {
        navigate("/cert/" + rowData.request_id);
      } else if (rowData.request_type === "Motor Vehicle Change of Ownership") {
        navigate("/cowcert/" + rowData.request_id);
      }
    }
  };

  return (
    <div className="bg-gray-100 min-h-screen">
      <Header />
      <div className="mt-8 px-4 lg:px-16 bg-gray-100 shadow-xs">
        
        <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-4 lg:space-y-0">
          <div className="text-xl font-semibold text-gray-800">Requests</div>
          
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="text"
              className="rounded-lg h-10 px-3 border border-gray-300 w-full sm:w-auto"
              placeholder="Search Plate/Ref/No"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
            />
            <button
              onClick={handleSearch}
              className="bg-blue-600 text-white rounded-lg px-4 py-2 hover:bg-blue-700 transition-colors"
            >
              Search
            </button>
            
            {/* Payment Status Filter */}
            <select name="payment_status" value={filters.payment_status} onChange={handleFilterChange} className="rounded-lg h-10 px-2 border border-gray-300 text-sm">
              <option value="">Payment Status</option>
              <option value="Paid">Paid</option>
              <option value="Unpaid">Unpaid</option>
            </select>

            {/* Owner/ID Validation Filter */}
            <select name="owner_status" value={filters.owner_status} onChange={handleFilterChange} className="rounded-lg h-10 px-2 border border-gray-300 text-sm">
              <option value="">ID Validation</option>
              <option value="Verified">Verified</option>
              <option value="Not Verified">Not Verified</option>
            </select>

            {/* Vehicle Status Filter */}
            <select name="vehicle_status" value={filters.vehicle_status} onChange={handleFilterChange} className="rounded-lg h-10 px-2 border border-gray-300 text-sm">
              <option value="">Vehicle Status</option>
              <option value="Valid">Valid</option>
              <option value="Conflict">Conflict</option>
            </select>

            {/* CMR Request Status Filter */}
            <select name="status" value={filters.status} onChange={handleFilterChange} className="rounded-lg h-10 px-2 border border-gray-300 text-sm">
              <option value="">CMR Status</option>
              <option value="Approved">Approved</option>
              <option value="Processing">Processing</option>
              <option value="Pending">Pending</option>
              <option value="Error">Error</option>
            </select>

            <Link
              to="/dashboard"
              className="cursor-pointer font-semibold text-blue-600 hover:text-blue-800 text-sm flex items-center ml-2"
            >
              <AiOutlinePlus className="mr-1" />
              Dashboard
            </Link>
          </div>
        </div>

        <div className="py-6">
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table {...getTableProps()} className="w-full whitespace-nowrap">
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()} className="bg-blue-50 border-b border-gray-200">
                    {headerGroup.headers.map((column) => (
                      <th {...column.getHeaderProps()} className="py-3 px-4 text-sm font-semibold text-gray-700 text-left">
                        {column.render("Header")}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              
              {loading ? (
                <tbody>
                  <tr>
                    <td colSpan={columns.length} className="py-20 text-center">
                      <ClipLoader color={"#2563EB"} size={40} />
                      <p className="mt-2 text-gray-500 font-medium">Loading requests...</p>
                    </td>
                  </tr>
                </tbody>
              ) : (
                <>
                  {result.length === 0 ? (
                    <tbody>
                      <tr>
                        <td colSpan={columns.length}>
                          <div className="flex flex-col items-center justify-center py-12">
                            <img alt="empty" src={EmptyList} className="h-32 opacity-50 mb-4" />
                            <span className="text-gray-500 font-medium">No records found matching criteria</span>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  ) : (
                    <tbody {...getTableBodyProps()}>
                      {page.map((row) => {
                        prepareRow(row);
                        return (
                          <tr {...row.getRowProps()} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                            {row.cells.map((cell) => {
                              return (
                                <td {...cell.getCellProps()} className="py-3 px-4 text-sm text-gray-800" onClick={() => handleCellClick(cell)}>
                                  {cell.render("Cell")}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  )}
                </>
              )}
            </table>
            
            {/* Pagination Controls */}
            {totalRecords > 0 && (
              <div className="flex flex-col sm:flex-row justify-between items-center p-4 bg-gray-50 border-t border-gray-200">
                <div className="mb-4 sm:mb-0 text-gray-600 text-sm">
                  Showing <span className="font-semibold text-gray-900">{totalRecords === 0 ? 0 : (pageIndex * pageSize) + 1}</span> to <span className="font-semibold text-gray-900">{Math.min((pageIndex + 1) * pageSize, totalRecords)}</span> of <span className="font-semibold text-gray-900">{totalRecords}</span> entries
                </div>
                
                <div className="flex items-center space-x-2">
                  <button className="px-3 py-1 bg-white border border-gray-300 rounded text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" onClick={() => gotoPage(0)} disabled={!canPreviousPage} type="button">First</button>
                  <button className="px-3 py-1 bg-white border border-gray-300 rounded text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" onClick={() => previousPage()} disabled={!canPreviousPage} type="button">Prev</button>
                  <span className="px-3 text-sm text-gray-600">
                    Page <span className="font-semibold text-gray-900">{pageIndex + 1}</span> of <span className="font-semibold text-gray-900">{pageCount === 0 ? 1 : pageCount}</span>
                  </span>
                  <button className="px-3 py-1 bg-white border border-gray-300 rounded text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" onClick={() => nextPage()} disabled={!canNextPage} type="button">Next</button>
                  <button className="px-3 py-1 bg-white border border-gray-300 rounded text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage} type="button">Last</button>
                </div>

                <div className="mt-4 sm:mt-0">
                  <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className="border border-gray-300 rounded-md text-sm text-gray-700 px-3 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                    {[10, 20, 30, 50, 100].map((size) => (
                      <option key={size} value={size}>Show {size}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}